import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/reducers';
import { getInstallmentsByPaymentCard, saveInstallment } from '../../../store/actions/installmentActions';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Grid,
    InputAdornment,
    Chip
} from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef } from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { format, parseISO, isValid } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';

const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    try {
        const parsed = parseISO(dateStr);
        return isValid(parsed) ? format(parsed, 'dd/MM/yyyy') : '-';
    } catch {
        return '-';
    }
};

interface EditFormData {
    id: number;
    Amount: string;
    DueDate: string;
    Discount: string;
    Interest: string;
    Comment: string;
}

const Installments: React.FC = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const installments = useSelector((state: RootState) => state.installments.installments);
    const isFetching = useSelector((state: RootState) => state.installments.isFetching);

    const [searchValue, setSearchValue] = useState('');
    const [searched, setSearched] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editForm, setEditForm] = useState<EditFormData>({
        id: 0,
        Amount: '',
        DueDate: '',
        Discount: '',
        Interest: '',
        Comment: '',
    });

    const handleSearch = () => {
        if (!searchValue.trim()) {
            enqueueSnackbar('Informe o número do carnê', { variant: 'warning' });
            return;
        }
        // @ts-ignore
        dispatch(getInstallmentsByPaymentCard(searchValue.trim()));
        setSearched(true);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleEditOpen = (installment: any) => {
        setEditForm({
            id: installment.id,
            Amount: String(installment.Amount || ''),
            DueDate: installment.DueDate ? installment.DueDate.substring(0, 10) : '',
            Discount: String(installment.Discount || '0'),
            Interest: String(installment.Interest || '0'),
            Comment: installment.Comment || '',
        });
        setEditDialogOpen(true);
    };

    const handleEditSave = () => {
        const amount = parseFloat(editForm.Amount);
        if (isNaN(amount) || amount < 0) {
            enqueueSnackbar('Valor inválido', { variant: 'error' });
            return;
        }

        // @ts-ignore
        dispatch(saveInstallment({
            id: editForm.id,
            Amount: amount,
            DueDate: editForm.DueDate ? `${editForm.DueDate} 03:00:00` : undefined,
            Discount: parseFloat(editForm.Discount) || 0,
            Interest: parseFloat(editForm.Interest) || 0,
            Comment: editForm.Comment,
        }));

        enqueueSnackbar('Parcela atualizada com sucesso', { variant: 'success' });
        setEditDialogOpen(false);

        // Refresh
        setTimeout(() => {
            if (searchValue.trim()) {
                // @ts-ignore
                dispatch(getInstallmentsByPaymentCard(searchValue.trim()));
            }
        }, 500);
    };

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'PaymentCardNumber',
            label: 'Carnê',
            options: { filter: true, sort: true }
        },
        {
            name: 'NumberInEnrollment',
            label: 'Mensalidade #',
            options: { filter: false, sort: true }
        },
        {
            name: 'DueDate',
            label: 'Vencimento',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => formatDate(value)
            }
        },
        {
            name: 'Amount',
            label: 'Valor',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: number) => formatBRL(value || 0)
            }
        },
        {
            name: 'DataPagto',
            label: 'Data Pagto',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: string) => formatDate(value)
            }
        },
        {
            name: 'Discount',
            label: 'Desconto',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value: number) => value ? formatBRL(value) : '-'
            }
        },
        {
            name: 'Interest',
            label: 'Juros',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value: number) => value ? formatBRL(value) : '-'
            }
        },
        {
            name: 'AmountPaid',
            label: 'Total',
            options: {
                filter: false,
                sort: true,
                customBodyRender: (value: number) => value ? formatBRL(value) : '-'
            }
        },
        {
            name: 'Pago',
            label: 'Pago',
            options: {
                filter: true,
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    const row = installments[dataIndex];
                    const paid = row?.AmountPaid && row.AmountPaid > 0;
                    return (
                        <Chip
                            label={paid ? 'Sim' : 'Não'}
                            color={paid ? 'success' : 'default'}
                            size="small"
                        />
                    );
                }
            }
        },
        {
            name: 'Saldo',
            label: 'Saldo',
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    const row = installments[dataIndex];
                    const saldo = row ? (row.Amount || 0) - (row.AmountPaid || 0) : 0;
                    return (
                        <Typography
                            variant="body2"
                            color={saldo > 0 ? 'error' : 'success.main'}
                            fontWeight={saldo > 0 ? 'bold' : 'normal'}
                        >
                            {formatBRL(Math.max(saldo, 0))}
                        </Typography>
                    );
                }
            }
        },
        {
            name: 'Comment',
            label: 'Comentário',
            options: {
                filter: false,
                sort: false,
                customBodyRender: (value: string) => value || '-'
            }
        },
        {
            name: 'id',
            label: 'Ações',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const row = installments[dataIndex];
                    return (
                        <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditOpen(row);
                            }}
                        >
                            Editar
                        </Button>
                    );
                }
            }
        },
    ];

    const tableOptions = {
        filterType: 'dropdown' as const,
        responsive: 'standard' as const,
        selectableRows: 'none' as const,
        rowsPerPage: 50,
        rowsPerPageOptions: [15, 30, 50],
        download: false,
        print: false,
        textLabels: {
            body: {
                noMatch: searched ? 'Nenhuma parcela encontrada para este carnê' : 'Pesquise um número de carnê',
                toolTip: 'Ordenar',
            },
            pagination: {
                next: 'Próxima Página',
                previous: 'Página Anterior',
                rowsPerPage: 'Registros por página:',
                displayRows: 'de',
            },
            toolbar: {
                search: 'Pesquisar',
                viewColumns: 'Ver colunas',
                filterTable: 'Filtrar',
            },
            filter: {
                all: 'Todos',
                title: 'Filtros',
                reset: 'Reiniciar',
            },
        },
    };

    // Calculate summary
    const totalAmount = installments.reduce((acc: number, i: any) => acc + (i.Amount || 0), 0);
    const totalPaid = installments.reduce((acc: number, i: any) => acc + (i.AmountPaid || 0), 0);
    const totalBalance = Math.max(totalAmount - totalPaid, 0);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Parcelas</Typography>
                <Box />
            </Box>

            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        label="Número do Carnê"
                        variant="outlined"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        size="small"
                        sx={{ minWidth: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button variant="contained" onClick={handleSearch} disabled={isFetching}>
                        Buscar
                    </Button>
                </Box>
            </Paper>

            {searched && installments.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Total Parcelas</Typography>
                            <Typography variant="h6" fontWeight="bold">{formatBRL(totalAmount)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Total Pago</Typography>
                            <Typography variant="h6" fontWeight="bold" color="success.main">{formatBRL(totalPaid)}</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Saldo em Aberto</Typography>
                            <Typography variant="h6" fontWeight="bold" color="error">{formatBRL(totalBalance)}</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {isFetching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper elevation={2}>
                    <MUIDataTable
                        title="Lista de Parcelas"
                        data={installments}
                        columns={columns}
                        options={tableOptions}
                    />
                </Paper>
            )}

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Editar Parcela</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Valor (R$)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={editForm.Amount}
                                onChange={(e) => setEditForm({ ...editForm, Amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Vencimento"
                                type="date"
                                fullWidth
                                variant="outlined"
                                value={editForm.DueDate}
                                onChange={(e) => setEditForm({ ...editForm, DueDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Desconto (R$)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={editForm.Discount}
                                onChange={(e) => setEditForm({ ...editForm, Discount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Juros (R$)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={editForm.Interest}
                                onChange={(e) => setEditForm({ ...editForm, Interest: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Comentário"
                                fullWidth
                                variant="outlined"
                                multiline
                                rows={2}
                                value={editForm.Comment}
                                onChange={(e) => setEditForm({ ...editForm, Comment: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleEditSave} variant="contained">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Installments;
